import {
  CHASE_DIRECT_LINK_ELIGIBLE_SLUGS,
  CHASE_DIRECT_OFFER_IDS,
  CHASE_SAPPHIRE_RESERVE_OFFER_ID,
} from './constants';
import type { ExperimentState, TransformableOffer, TransformContext } from './types';

/**
 * Transforms a single offer based on experiment variants.
 * Pure function - no hooks, no side effects, works in any context (client or server).
 *
 * @param offer - The offer to transform
 * @param experiments - Current experiment variants
 * @param context - Context needed for transformation (slug, mobile, etc.)
 * @returns Transformed offer with updated applyLink if experiments apply
 */
export async function transformOfferWithExperiments<T extends TransformableOffer>(
  offer: T,
  experiments: ExperimentState,
  context: TransformContext,
): Promise<T> {
  let transformed = { ...offer };

  // Mobile direct-link for CFU/CSP is always on (not experiment-gated)
  transformed = applyMobileChaseDirectLink(transformed, context);

  // Chase Direct Link for CSR (shp_1480_csr_direct_to_application) on its review
  transformed = applyCSRDirectLink(transformed, context);

  return transformed;
}

/**
 * Batch transform multiple offers.
 *
 * @param offers - Array of offers to transform
 * @param experiments - Current experiment variants
 * @param context - Context needed for transformation
 * @returns Array of transformed offers
 */
export async function transformOffersWithExperiments<T extends TransformableOffer>(
  offers: T[],
  experiments: ExperimentState,
  context: TransformContext,
): Promise<T[]> {
  const results: T[] = [];

  for (const offer of offers) {
    const transformed = await transformOfferWithExperiments(offer, experiments, context);
    results.push(transformed);
  }

  return results;
}

// ============================================================================
// Experiment-Specific Transformation Functions
// ============================================================================

/**
 * Always-on mobile direct-link for Chase Freedom Unlimited and Sapphire Preferred.
 * Not gated by any experiment — this is the baseline behavior.
 */
function applyMobileChaseDirectLink<T extends TransformableOffer>(
  offer: T,
  context: TransformContext,
): T {
  const { isMobile } = context;

  if (!isMobile || !offer.details.isMonetizing || !CHASE_DIRECT_OFFER_IDS.includes(offer.id)) {
    return offer;
  }

  return {
    ...offer,
    details: {
      ...offer.details,
      applyLink: offer.details.directApplyLink ?? offer.details.applyLink,
    },
  };
}

/**
 * Always-on direct-link for Chase Sapphire Reserve on its review.
 * Not gated by any experiment — this is the baseline behavior.
 */
function applyCSRDirectLink<T extends TransformableOffer>(offer: T, context: TransformContext): T {
  const { slug } = context;

  if (
    !CHASE_DIRECT_LINK_ELIGIBLE_SLUGS.includes(slug) ||
    !offer.details.isMonetizing ||
    offer.id !== CHASE_SAPPHIRE_RESERVE_OFFER_ID
  ) {
    return offer;
  }

  return {
    ...offer,
    details: {
      ...offer.details,
      applyLink: offer.details.directApplyLink ?? offer.details.applyLink,
    },
  };
}
